import { useTheme } from '@chronosphereio/chrono-ui';
import { PieChart as EChartsPieChart, PieSeriesOption } from 'echarts/charts';
import { DatasetComponent, GridComponent, TooltipComponent } from 'echarts/components';
import { EChartsCoreOption, use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useEffect, useMemo, useRef } from 'react';
import { ContentDimensions, DisplayOption, UnitOptions } from '@/core';
import { DEFAULT_DISPLAY } from '@/panels-plugin/plugins/pie-chart/pie-chart-model';
import { LabelFormatString } from '@/panels-plugin/plugins/pie-chart/utils';
import { useChartsTheme } from '../context/ChartsProvider';
import { EChart } from '../EChart';
import { formatTooltip, formatValueLabel } from './utils';

const CONSTANT_OPTIONS: EChartsCoreOption = {
  series: [
    {
      type: 'pie',
      emphasis: {
        itemStyle: {
          borderWidth: 0,
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: 'rgba(0, 0, 0, 0.5)',
        },
      },
    },
  ],
};

// eslint-disable-next-line react-hooks/rules-of-hooks
use([EChartsPieChart, GridComponent, DatasetComponent, TooltipComponent, CanvasRenderer]);

/**
 * Define the PieChartDataset type as an array of tuples containing 4 elements:
 * 1. A string representing the label of the slice.
 * 2. A number (or null) representing the value of the slice.
 * 3. A string representing the color of the slice.
 * 4. The series id(s) for the slice. Corresponds to the series id(s) in the legend. Typically is just a string,
 * but for the aggregated slice, it is an array since a single slice corresponds to multiple legend items.
 */
export type PieChartDataset = Array<[string, number | null, string, string | string[]]>;

export interface PieChartBaseProps extends ContentDimensions {
  /**
   * An array of the series that have been aggregated into a single slice
   */
  aggregatedSeries: PieChartDataset;
  dataset: PieChartDataset | null;
  display?: DisplayOption;
  /**
   * Whether to show the value, percent or both inside the pie slices
   * @default 'Percent'
   */
  labelFormat?: LabelFormatString;
  /**
   * Whether or not to show the label lines that render the series name outside the pie slices
   * @default true
   */
  showSeriesName?: boolean;
  unit?: UnitOptions;
  /**
   * The number of decimal places to show in the percent label
   * @default 0
   */
  percentDecimalPlaces?: number;
}

/**
 * Use the context-aware `PieChart.Viz` instead.
 * @deprecated
 */
export function PieChartBase(props: PieChartBaseProps) {
  const {
    width,
    height,
    aggregatedSeries,
    dataset,
    display = DEFAULT_DISPLAY,
    labelFormat = 'Percent',
    showSeriesName = true,
    unit = { kind: 'Decimal' },
    percentDecimalPlaces = 0,
  } = props;

  const chartsTheme = useChartsTheme();
  const { theme } = useTheme();

  const commonSeriesStyles = useMemo(() => {
    return {
      itemStyle: {
        borderWidth: 1,
        borderColor: theme.palette.background.container,
        color: (params: { dataIndex: number; value: [string, number | null, string] }) => params.value[2],
      },
      radius: display === 'Donut' ? ['40%', '75%'] : '75%',
      percentPrecision: percentDecimalPlaces,
      animation: false,
    };
  }, [display, percentDecimalPlaces, theme.palette.background.container]);

  const series = useMemo(() => {
    return [
      // ECharts does not natively support displaying labels both inside and outside of the pie chart.
      // To get around this limitation, we use a workaround involving two pie charts:
      // 1. One pie chart for the names with label lines outside the slices.
      // 2. Another for the value labels inside the slices.
      // See https://github.com/apache/echarts/issues/18383 for details.
      showSeriesName && {
        type: 'pie',
        ...commonSeriesStyles,
        label: {
          show: true,
          position: 'outside',
          color: theme.palette.text.primary,
        },
        labelLine: {
          // Length of the second segment of the label line
          length2: 15,
        },
      },
      ...(CONSTANT_OPTIONS.series as PieSeriesOption[]).map((seriesItem) => ({
        ...seriesItem,
        ...commonSeriesStyles,
        label: {
          show: true,
          position: 'inside',
          formatter: (params: { color: string; name: string; value: [string, number | null]; percent: number }) =>
            formatValueLabel(params, labelFormat, unit),
        },
      })),
    ];
  }, [commonSeriesStyles, labelFormat, showSeriesName, theme.palette.text.primary, unit]);

  const valuesSum = useMemo(() => {
    return dataset ? dataset.reduce((sum, item) => sum + (item[1] ?? 0), 0) : 0;
  }, [dataset]);

  // track the mouse x position so we can adjust the tooltip position if it is going to render off the screen
  const mouseXPosition = useRef(0);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mouseXPosition.current = e.clientX;
    };
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const tooltip = useMemo(() => {
    return {
      appendToBody: true,
      backgroundColor: theme.palette.background.popover,
      borderWidth: 0,
      textStyle: {
        color: theme.palette.text.primary,
        fontSize: 12,
      },
      trigger: 'item',
      position: (
        point: [number, number],
        params: never,
        dom: never,
        rect: never,
        size: { contentSize: [number, number] }
      ) => {
        const tooltipWidth = size.contentSize[0];

        // If the tooltip is going to run off the right side of the screen, position it to the left instead
        if (mouseXPosition.current + tooltipWidth > window.innerWidth) {
          return 'left';
        }
        // Otherwise, position as normal
        return [point[0], point[1]];
      },
      formatter: (params: { color: string; name: string; value: [string, number | null]; percent: number }) =>
        formatTooltip({ labelFormat, params, unit, aggregatedSeries, valuesSum, percentDecimalPlaces }),
    };
  }, [
    aggregatedSeries,
    mouseXPosition,
    labelFormat,
    percentDecimalPlaces,
    theme.palette.background.popover,
    theme.palette.text.primary,
    unit,
    valuesSum,
  ]);

  const option: EChartsCoreOption = useMemo(() => {
    if (dataset == null || !dataset.length) return chartsTheme.noDataOption;

    return {
      ...CONSTANT_OPTIONS,
      dataset: {
        source: dataset,
      },
      series,
      tooltip,
    };
  }, [chartsTheme.noDataOption, dataset, series, tooltip]);

  return (
    <div style={{ width, height }}>
      <EChart width={width} height={height} option={option} theme={chartsTheme.echartsTheme} />
    </div>
  );
}
